import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

const EMPTY_GROUP = { icon:'🕊️', group_name:'', sub_title:'', is_accent:false, items:[], display_order:0 };
const EMPTY_ITEM  = { t:'', n:'' };

const css = {
  card:    { background:'#fff', border:'1px solid #e8d8b0', borderRadius:14, padding:'20px 22px', marginBottom:12, boxShadow:'0 2px 12px rgba(0,0,0,0.06)' },
  label:   { display:'block', fontSize:'0.66rem', letterSpacing:'0.12em', textTransform:'uppercase', color:'#6b5e4e', marginBottom:5 },
  input:   { width:'100%', padding:'10px 14px', background:'#fdf8f0', border:'1.5px solid #d4b896', borderRadius:9, color:'#1a2a4a', fontSize:'0.88rem', outline:'none', fontFamily:"'Raleway',sans-serif", boxSizing:'border-box' },
  btnGold: { background:'linear-gradient(135deg,#c9a84c,#8b6914)', border:'none', color:'#070f22', padding:'10px 22px', borderRadius:9, fontSize:'0.76rem', fontWeight:700, cursor:'pointer', letterSpacing:'0.08em', fontFamily:"'Raleway',sans-serif" },
  btnGray: { background:'#f0ebe0', border:'1px solid #d4b896', color:'#6b5e4e', padding:'9px 16px', borderRadius:8, fontSize:'0.72rem', cursor:'pointer', fontFamily:"'Raleway',sans-serif" },
  btnRed:  { background:'rgba(178,34,34,0.18)', border:'1px solid rgba(178,34,34,0.4)', color:'#cc2222', padding:'7px 14px', borderRadius:8, fontSize:'0.72rem', cursor:'pointer', fontFamily:"'Raleway',sans-serif" },
  btnGreen:{ background:'linear-gradient(135deg,#2d7a2d,#1a5c1a)', border:'none', color:'#fff', padding:'10px 22px', borderRadius:9, fontSize:'0.76rem', fontWeight:700, cursor:'pointer', letterSpacing:'0.08em', fontFamily:"'Raleway',sans-serif" },
};

export default function ServicesTab() {
  const [groups, setGroups]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null); // null = new
  const [form, setForm]         = useState(EMPTY_GROUP);

  const flash = (m) => { setMsg(m); setTimeout(()=>setMsg(''),4000); };

  const fetch = async () => {
    setLoading(true);
    const { data } = await supabase.from('service_times').select('*').order('display_order',{ascending:true});
    setGroups(data||[]);
    setLoading(false);
  };

  useEffect(()=>{ fetch(); },[]);

  const openNew = () => {
    setForm({...EMPTY_GROUP, display_order: groups.length});
    setEditItem(null);
    setShowForm(true);
    setTimeout(()=>window.scrollTo({top:99999,behavior:'smooth'}),50);
  };

  const openEdit = (g) => {
    const items = typeof g.items === 'string' ? JSON.parse(g.items) : (g.items||[]);
    setForm({...g, items});
    setEditItem(g.id);
    setShowForm(true);
    setTimeout(()=>window.scrollTo({top:99999,behavior:'smooth'}),50);
  };

  const cancel = () => { setShowForm(false); setEditItem(null); setForm(EMPTY_GROUP); };

  const save = async () => {
    if(!form.group_name.trim()) { flash('❌ Group name is required'); return; }
    if(!form.items.length)      { flash('❌ Add at least one time item'); return; }
    for(const it of form.items){
      if(!it.t.trim()||!it.n.trim()){ flash('❌ All time items need a time and name'); return; }
    }
    setSaving(true);
    const payload = { ...form, items: JSON.stringify(form.items) };
    let error;
    if(editItem){
      ({ error } = await supabase.from('service_times').update(payload).eq('id',editItem));
    } else {
      delete payload.id;
      ({ error } = await supabase.from('service_times').insert(payload));
    }
    setSaving(false);
    if(error){ flash('❌ Save failed: '+error.message); return; }
    flash(editItem ? '✅ Service group updated!' : '✅ Service group added!');
    cancel();
    fetch();
  };

  const del = async (id) => {
    if(!window.confirm('Delete this service group?')) return;
    await supabase.from('service_times').delete().eq('id',id);
    flash('🗑 Deleted');
    fetch();
  };

  // ── form helpers
  const setField = (k,v) => setForm(f=>({...f,[k]:v}));
  const addItem  = ()    => setForm(f=>({...f,items:[...f.items,{...EMPTY_ITEM}]}));
  const delItem  = (i)   => setForm(f=>({...f,items:f.items.filter((_,j)=>j!==i)}));
  const setItem  = (i,k,v) => setForm(f=>{
    const items=[...f.items]; items[i]={...items[i],[k]:v}; return {...f,items};
  });

  return (
    <div>
      <div style={{background:'rgba(184,134,11,0.08)',border:'1px solid #d4b896',borderRadius:10,padding:'12px 16px',marginBottom:20,fontSize:'0.82rem',color:'#5a4e3e',lineHeight:1.6}}>
        🕐 <strong style={{color:'#8b6508'}}>Service Times:</strong> Add, edit, or remove service groups. Each group can have multiple time slots. Changes appear instantly on the website.
      </div>

      {msg && (
        <div style={{background: msg.startsWith('✅')?'#f0fff0':'#fff0f0', border:`1px solid ${msg.startsWith('✅')?'#5cb85c':'#ff6b6b'}`, borderRadius:8, padding:'10px 16px', marginBottom:16, fontSize:'0.84rem', color: msg.startsWith('✅')?'#1d5c1d':'#cc0000'}}>
          {msg}
        </div>
      )}

      <div style={{display:'flex',gap:10,marginBottom:20,flexWrap:'wrap',alignItems:'center'}}>
        <button style={css.btnGold} onClick={openNew}>➕ Add Service Group</button>
        <button style={css.btnGray} onClick={fetch}>🔄 Refresh</button>
        <span style={{marginLeft:'auto',fontSize:'0.78rem',color:'#6b5e4e'}}>{groups.length} group{groups.length!==1?'s':''}</span>
      </div>

      {loading ? (
        <div style={{textAlign:'center',padding:40,color:'#8b6508',fontSize:'0.88rem'}}>Loading…</div>
      ) : groups.length===0 ? (
        <div style={{...css.card,textAlign:'center',color:'#9e8f7a',padding:40}}>
          <div style={{fontSize:'2rem',marginBottom:12}}>🕐</div>
          No service groups yet. Click <strong>Add Service Group</strong> to get started.
        </div>
      ) : (
        groups.map((g,idx)=>{
          const items = typeof g.items==='string' ? JSON.parse(g.items) : (g.items||[]);
          return (
            <div key={g.id||idx} style={{...css.card,borderLeft:`4px solid ${g.is_accent?'#c9a84c':'#d4b896'}`}}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
                <div style={{flex:1}}>
                  <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6}}>
                    <span style={{fontSize:'1.5rem'}}>{g.icon}</span>
                    <div>
                      <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'0.88rem',color:'#8b6508'}}>{g.group_name}</div>
                      <div style={{fontSize:'0.72rem',color:'#9e8f7a',marginTop:2}}>{g.sub_title}</div>
                    </div>
                    {g.is_accent&&<span style={{fontSize:'0.62rem',background:'rgba(201,168,76,0.2)',border:'1px solid rgba(201,168,76,0.4)',color:'#8b6508',padding:'2px 8px',borderRadius:10,letterSpacing:'0.08em'}}>FEATURED</span>}
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:4,paddingLeft:8}}>
                    {items.map((it,i)=>(
                      <div key={i} style={{fontSize:'0.78rem',color:'#5a4e3e'}}>
                        <strong style={{color:'#8b6508',fontFamily:"'Cinzel Decorative',serif",fontSize:'0.72rem'}}>{it.t||it.time}</strong>
                        {' — '}{it.n||it.name}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{display:'flex',gap:8,flexShrink:0}}>
                  <button style={{...css.btnGray,color:'#8b6508',border:'1px solid rgba(201,168,76,0.3)'}} onClick={()=>openEdit(g)}>✏️ Edit</button>
                  <button style={css.btnRed} onClick={()=>del(g.id)}>🗑</button>
                </div>
              </div>
            </div>
          );
        })
      )}

      {/* ── Add / Edit Form ── */}
      {showForm && (
        <div style={{...css.card,border:'2px solid rgba(201,168,76,0.5)',marginTop:24}}>
          <h3 style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'0.88rem',color:'#8b6508',marginBottom:20}}>
            {editItem ? '✏️ Edit Service Group' : '➕ New Service Group'}
          </h3>

          <div style={{display:'grid',gridTemplateColumns:'80px 1fr 1fr',gap:12,marginBottom:14}}>
            <div>
              <label style={css.label}>Icon</label>
              <input style={css.input} value={form.icon} onChange={e=>setField('icon',e.target.value)} placeholder="🕊️" maxLength={4}/>
            </div>
            <div>
              <label style={css.label}>Group Name *</label>
              <input style={css.input} value={form.group_name} onChange={e=>setField('group_name',e.target.value)} placeholder="Sunday Worship"/>
            </div>
            <div>
              <label style={css.label}>Sub-title</label>
              <input style={css.input} value={form.sub_title} onChange={e=>setField('sub_title',e.target.value)} placeholder="Every Sunday"/>
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:20}}>
            <div>
              <label style={css.label}>Display Order</label>
              <input type="number" style={css.input} value={form.display_order} onChange={e=>setField('display_order',Number(e.target.value))} min={0}/>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:12,paddingTop:22}}>
              <div
                onClick={()=>setField('is_accent',!form.is_accent)}
                style={{width:48,height:26,borderRadius:13,cursor:'pointer',background:form.is_accent?'#c9a84c':'#ccc',position:'relative',transition:'background 0.25s',flexShrink:0}}
              >
                <div style={{position:'absolute',top:3,left:form.is_accent?25:3,width:20,height:20,borderRadius:'50%',background:'#fff',transition:'left 0.25s',boxShadow:'0 2px 4px rgba(0,0,0,0.2)'}}/>
              </div>
              <span style={{fontSize:'0.78rem',color:'#6b5e4e',fontWeight:700}}>Featured / Accent Style</span>
            </div>
          </div>

          {/* Time Items */}
          <div style={{marginBottom:16}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
              <label style={{...css.label,marginBottom:0}}>Time Slots *</label>
              <button style={{...css.btnGray,fontSize:'0.7rem',padding:'5px 12px'}} onClick={addItem}>+ Add Time</button>
            </div>
            {form.items.length===0 && (
              <div style={{fontSize:'0.78rem',color:'#9e8f7a',padding:'12px 0'}}>No time slots yet — click <strong>+ Add Time</strong> above.</div>
            )}
            {form.items.map((it,i)=>(
              <div key={i} style={{display:'grid',gridTemplateColumns:'140px 1fr auto',gap:8,marginBottom:8,alignItems:'center'}}>
                <input style={css.input} value={it.t} onChange={e=>setItem(i,'t',e.target.value)} placeholder="06:30 AM"/>
                <input style={css.input} value={it.n} onChange={e=>setItem(i,'n',e.target.value)} placeholder="Service 1 — Morning Worship"/>
                <button style={{...css.btnRed,padding:'9px 12px'}} onClick={()=>delItem(i)}>✕</button>
              </div>
            ))}
          </div>

          <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
            <button style={css.btnGreen} onClick={save} disabled={saving}>
              {saving ? '⏳ Saving…' : editItem ? '💾 Update Group' : '💾 Save Group'}
            </button>
            <button style={css.btnGray} onClick={cancel}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
